/**
 * Start a read-only demo session and land the visitor inside the app.
 *
 * Mints a demo JWT (`POST /api/v1/public/demo/start`), persists it under the
 * same localStorage keys the normal auth flow uses, fetches the demo user so the
 * shell can hydrate, and marks the session as a demo. Demo tokens are short-lived
 * and have NO refresh token — when they expire the standard session-expired path
 * signs the visitor out.
 *
 * Callers should redirect to the app root afterwards (a full navigation, so
 * AuthProvider re-bootstraps from the freshly-stored token + user).
 */

import { authApi } from './authApi';
import { demoApi } from './demoApi';
import { setDemoMode } from './demoSession';
import { resetSessionExpiredGuard } from '../api/axiosInstance';
import { clearRefreshToken } from './secureTokenStore';
import { AUTH_STORAGE_KEYS } from './storageKeys';

export async function enterDemo(variation?: string): Promise<void> {
  const session = await demoApi.start(variation);

  localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, session.access_token);
  // Demo sessions are not refreshable — make sure no stale refresh token
  // lingers. This MUST go through the store rather than localStorage: on a
  // native build the previous real user's token is in the Keychain, and when
  // the short-lived demo access token expires the refresh path would find it
  // and mint a genuine, mutable session behind the demo banner.
  await clearRefreshToken();
  resetSessionExpiredGuard();

  const user = await authApi.getMe(session.access_token);
  localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(user));

  setDemoMode(session.variation.key);
}
